import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet, planningPath } from '../services/PlanningApiClient';
import { fmtId } from '../utils/jiraId';

/** One linked record, as returned by /vendors/:id/links. */
interface LinkItem {
  id: string;
  label: string | null;
  status: string | null;
}

interface LinkGroup {
  type: 'quote' | 'booking' | 'procurement_order' | 'budget_item';
  label: string;
  items: LinkItem[];
}

export interface VendorLinksResponse {
  total: number;
  groups: LinkGroup[];
}

/** Where clicking a linked record takes you. Bookings and procurement orders
 *  have no route: those screens were removed because their API is disabled, so
 *  those rows are listed but not clickable. */
const ROUTE_FOR: Record<LinkGroup['type'], ((id: string) => string) | undefined> = {
  quote:             id => `/quotes/${fmtId('QT', id)}`,
  booking:           undefined,
  procurement_order: undefined,
  budget_item:       () => `/budget`,
};

/** Shared fetch for everything pointing at this vendor. */
export async function loadVendorLinks(vendorId: string): Promise<VendorLinksResponse> {
  return apiGet<VendorLinksResponse>(planningPath(`/vendors/${vendorId}/links`));
}

interface VendorLinksProps {
  /** vendors.vendor_id slug or numeric id — the endpoint accepts either. */
  vendorId: string;
}

/**
 * Everything pointing at this vendor, grouped by type and clickable — the
 * vendor page previously gave no way to tell whether a vendor was actually
 * referenced anywhere, or to reach what referenced it.
 */
const VendorLinks: React.FC<VendorLinksProps> = ({ vendorId }) => {
  const navigate = useNavigate();
  const [data, setData] = useState<VendorLinksResponse | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!vendorId) return;
    setLoading(true);
    try {
      const res = await loadVendorLinks(vendorId);
      setData(res);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [vendorId]);

  useEffect(() => { void load(); }, [load]);

  if (loading || !data) return null;

  return (
    <section className="side-section">
      <h4>Linked Records {data.total > 0 && <span className="ds-badge ds-badge-neutral">{data.total}</span>}</h4>
      {data.total === 0 ? (
        <p className="form-hint">
          Nothing links to this vendor yet. Accepting a quote for them creates a booking and budget line automatically.
        </p>
      ) : (
        data.groups.map(group => (
          <div key={group.type} className="vl-group">
            <p className="vl-group-label">{group.label}</p>
            {group.items.map(item => {
              const routeFor = ROUTE_FOR[group.type];
              return (
                <button
                  key={`${group.type}-${item.id}`}
                  type="button"
                  className="vl-item"
                  disabled={!routeFor}
                  onClick={() => routeFor && navigate(routeFor(item.id))}
                >
                  <span className="vl-item-label">{item.label || `#${item.id}`}</span>
                  {item.status && <span className="ds-badge ds-badge-neutral">{item.status}</span>}
                </button>
              );
            })}
          </div>
        ))
      )}
    </section>
  );
};

export default VendorLinks;
